import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { openOrDownloadBlob } from '~/modules/covenants/utils.ts';
import { useGuaranteesPdfPreview } from '~/modules/guarantees/form/application/shared/hooks/useGuaranteesPdfPreview.ts';
import { downloadGuaranteesClientDocument } from '~/store/guarantees/thunks/applicationThunks.ts';
import type { AppDispatch } from '~/store/store.ts';
import { MANAGER_STEP3_MISSING_PDF_TEXT } from '../../../manager/step3/constants.ts';
import { CLIENT_STEP2_DOCUMENT_FALLBACK_FILE_NAME } from '../constants.ts';
import type { TClientGeneratedDocumentRow } from '../types.ts';

type TPreviewDocument = {
  data: Blob;
  contentType?: string;
};

export const useClientStep2PdfPreview = ({
  generatedDocument,
  hasApplicationFilesLoaded,
  isApplicationFilesLoading,
  setModalAlert,
  showGuaranteesRequestError,
}: {
  generatedDocument: TClientGeneratedDocumentRow | null;
  hasApplicationFilesLoaded: boolean;
  isApplicationFilesLoading: boolean;
  setModalAlert: (value: { text: string }) => void;
  showGuaranteesRequestError: (error: unknown) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [previewDocument, setPreviewDocument] = useState<TPreviewDocument | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const pdfPreview = useGuaranteesPdfPreview({
    contentType: previewDocument?.contentType,
    documentBlob: previewDocument?.data,
    missingPdfText: MANAGER_STEP3_MISSING_PDF_TEXT,
    setModalAlert,
  });
  const isDocumentLoading =
    isApplicationFilesLoading || isPreviewLoading || pdfPreview.isDocumentLoading;

  const handlePreviewDocument = useCallback(
    async (previewId: number | string) => {
      const documentFileId = String(previewId || '').trim();

      if (!documentFileId) {
        setModalAlert({ text: MANAGER_STEP3_MISSING_PDF_TEXT });

        return;
      }

      try {
        const downloadedDocument = await dispatch(
          downloadGuaranteesClientDocument({
            id: documentFileId,
            isAttached: false,
          })
        ).unwrap();

        openOrDownloadBlob({
          blob: downloadedDocument.data,
          fileName: downloadedDocument.filename,
          fallbackFileName: CLIENT_STEP2_DOCUMENT_FALLBACK_FILE_NAME,
          contentType: downloadedDocument.contentType,
        });
      } catch (error) {
        showGuaranteesRequestError(error);
      }
    },
    [dispatch, setModalAlert, showGuaranteesRequestError]
  );

  useEffect(() => {
    const documentFileId = generatedDocument?.fileId;

    if (isApplicationFilesLoading) {
      return;
    }

    if (!hasApplicationFilesLoaded) {
      return;
    }

    if (!documentFileId) {
      setPreviewDocument(null);
      setModalAlert({ text: MANAGER_STEP3_MISSING_PDF_TEXT });

      return;
    }

    let isCancelled = false;

    const loadPreview = async () => {
      setPreviewDocument(null);
      setIsPreviewLoading(true);

      try {
        const downloadedDocument = await dispatch(
          downloadGuaranteesClientDocument({
            id: documentFileId,
            isAttached: false,
          })
        ).unwrap();

        if (isCancelled) {
          return;
        }

        setPreviewDocument({
          data: downloadedDocument.data,
          contentType: downloadedDocument.contentType,
        });
      } catch (error) {
        if (!isCancelled) {
          setPreviewDocument(null);
          showGuaranteesRequestError(error);
        }
      } finally {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      isCancelled = true;
    };
  }, [
    dispatch,
    generatedDocument?.fileId,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    setModalAlert,
    showGuaranteesRequestError,
  ]);

  return {
    pdfUrl: pdfPreview.pdfUrl,
    isDocumentLoading,
    handleOpenPdf: pdfPreview.handleOpenPdf,
    handlePreviewDocument,
  };
};
